import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GraduationCap, Star, Mic, X, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useLearning } from "@/context/LearningContext";
import { PracticeDialog } from "@/components/PracticeDialog";

export function PhrasePopup() {
  const { t } = useI18n();
  const { popup, dismiss, updatePopup } = useLearning();
  const navigate = useNavigate();
  const [practiceOpen, setPracticeOpen] = useState(false);

  useEffect(() => {
    if (!popup) return;
    const id = setTimeout(() => dismiss(), 15000);
    return () => clearTimeout(id);
  }, [popup, dismiss]);

  if (!popup) return null;

  const arabic = popup.arabic_text || popup.translation;
  const english = popup.english_text || popup.original_text;

  const toggleSave = async () => {
    try {
      const { data } = await api.post(`/learning/phrases/${popup.phrase_id}/save`);
      updatePopup({ saved: data.saved });
    } catch (e) { console.error("Failed to toggle save:", e); }
  };

  return (
    <>
      <div
        className="fixed z-50 bottom-4 end-4 w-[340px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl border border-border shadow-2xl p-4 animate-pop"
        data-testid="phrase-popup"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-accent">
            <div className="h-7 w-7 rounded-lg bg-accent/10 grid place-items-center"><GraduationCap size={16} /></div>
            <span className="text-xs font-semibold uppercase tracking-wide">{t("learnPopupTitle")}</span>
          </div>
          <button onClick={dismiss} data-testid="phrase-popup-close" className="text-muted-foreground hover:text-foreground" title={t("dismiss")}>
            <X size={16} />
          </button>
        </div>

        <div className="text-lg font-semibold text-foreground leading-snug" dir="rtl">{arabic}</div>
        {popup.transliteration && <div className="text-sm text-accent italic mt-0.5">{popup.transliteration}</div>}
        <div className="text-sm text-muted-foreground mt-0.5">{english}</div>

        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={toggleSave}
            data-testid="phrase-popup-save"
            className={`flex items-center gap-1.5 h-9 px-3 rounded-full text-sm font-medium border transition-colors ${popup.saved ? "bg-accent/10 text-accent border-accent/30" : "border-border hover:bg-secondary"}`}
          >
            <Star size={15} className={popup.saved ? "fill-current" : ""} /> {popup.saved ? t("savedLabel") : t("saveToMyPhrases")}
          </button>
          <button
            onClick={() => setPracticeOpen(true)}
            data-testid="phrase-popup-practice"
            className="flex items-center gap-1.5 h-9 px-3 rounded-full text-sm font-medium bg-primary text-white hover:bg-primary/90"
          >
            <Mic size={15} /> {t("practiceThis")}
          </button>
          <button
            onClick={() => { dismiss(); navigate("/learning"); }}
            data-testid="phrase-popup-more"
            className="ms-auto text-muted-foreground hover:text-primary" title={t("learnMore")}
          >
            <ArrowRight size={16} className="rtl:rotate-180" />
          </button>
        </div>
      </div>

      <PracticeDialog
        phrase={{ phrase_id: popup.phrase_id, arabic, translit: popup.transliteration, english }}
        open={practiceOpen}
        onOpenChange={setPracticeOpen}
        onMastered={() => {}}
      />
    </>
  );
}
